/* global process */
import { trackGoogle } from './GoogleAnalytics'
import { trackMeta } from './MetaPixel'
const ENDPOINT = process.env.GATSBY_ANALYTICS_ENDPOINT
const TOKEN = process.env.GATSBY_ANALYTICS_TOKEN
const QUEUE_KEY = 'mdf_analytics_queue_v1'
const ANONYMOUS_KEY = 'mdf_analytics_anonymous_id'
const SESSION_KEY = 'mdf_analytics_session'
const ATTRIBUTION_KEY = 'mdf_analytics_attribution'
const SESSION_TIMEOUT = 30 * 60 * 1000
const MAX_BATCH = 25

const isBrowser = () => typeof window !== 'undefined'
const uuid = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

const read = (key, fallback) => {
  if (!isBrowser()) return fallback
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback
  } catch (_) {
    return fallback
  }
}

const write = (key, value) => {
  if (!isBrowser()) return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (_) {
    // Storage can be unavailable in privacy mode; analytics remains best effort.
  }
}

const userId = () => {
  const raw = read('SessionIdMF', null)
  if (raw === null || raw === '' || raw === 'null') return null
  return String(raw)
}

const anonymousId = () => {
  let id = read(ANONYMOUS_KEY, null)
  if (!id) {
    id = `anon_${uuid()}`
    write(ANONYMOUS_KEY, id)
  }
  return id
}

const session = () => {
  const now = Date.now()
  let current = read(SESSION_KEY, null)
  const fresh = !current || now - current.last_activity > SESSION_TIMEOUT
  if (fresh) current = { id: `ses_${uuid()}`, started_at: now }
  current.last_activity = now
  write(SESSION_KEY, current)
  return { ...current, fresh }
}

const safeReferrer = () => {
  if (!document.referrer) return undefined
  try {
    const url = new URL(document.referrer)
    return `${url.origin}${url.pathname}`
  } catch (_) {
    return undefined
  }
}

const attribution = () => {
  const params = new URLSearchParams(window.location.search)
  const existing = read(ATTRIBUTION_KEY, {})
  const incoming = {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    referrer: safeReferrer()
  }
  const merged = Object.fromEntries(
    Object.entries({ ...existing, ...incoming }).filter(([, value]) => value)
  )
  if (Object.values(incoming).some(Boolean)) write(ATTRIBUTION_KEY, merged)
  return merged
}

const platform = () =>
  window.matchMedia('(max-width: 767px)').matches ? 'web_mobile' : 'web_desktop'

const enqueue = event => {
  const queue = read(QUEUE_KEY, [])
  queue.push(event)
  write(QUEUE_KEY, queue.slice(-500))
}

let sending = false
export async function flushAnalytics() {
  if (!isBrowser() || !ENDPOINT || sending || !navigator.onLine) return
  const queue = read(QUEUE_KEY, [])
  if (!queue.length) return
  const batch = queue.slice(0, MAX_BATCH)
  const headers = { 'Content-Type': 'application/json' }
  if (TOKEN) headers.Authorization = `Bearer ${TOKEN}`
  sending = true
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: batch }),
      keepalive: true
    })
    if (response.ok) {
      const sent = new Set(batch.map(event => event.event_id))
      write(QUEUE_KEY, read(QUEUE_KEY, []).filter(event => !sent.has(event.event_id)))
    }
  } catch (_) {
    // Analytics must never interrupt the purchase experience.
  } finally {
    sending = false
  }
}

export function track(eventName, fields = {}) {
  if (!isBrowser()) return null
  try { trackGoogle(eventName, fields) } catch {}
  try { trackMeta(eventName, fields) } catch {}
  if (!ENDPOINT) return null
  const currentSession = session()
  const event = {
    event_id: `evt_${uuid()}`,
    event_name: eventName,
    event_timestamp: new Date().toISOString(),
    platform: platform(),
    session_id: currentSession.id,
    anonymous_id: anonymousId(),
    user_id: userId(),
    page_path: window.location.pathname,
    ...attribution(),
    ...fields
  }
  enqueue(event)
  void flushAnalytics()
  return event.event_id
}

let lastPage = null
export function trackPage(pathname) {
  if (!isBrowser()) return
  const path = pathname || window.location.pathname
  if (lastPage === path) return
  const previous = lastPage ? `${window.location.origin}${lastPage}` : safeReferrer()
  lastPage = path
  const current = session()
  if (current.fresh) track('session_start')
  const location = new URL(path, window.location.origin)
  const incoming = new URLSearchParams(window.location.search)
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'gclid', 'dclid', 'gbraid', 'wbraid']) {
    if (incoming.has(key)) location.searchParams.set(key, incoming.get(key))
  }
  track('page_view', { page_path: path, page_location: location.href, page_referrer: previous, page_title: document.title })
}

export function initializeAnalytics() {
  if (!isBrowser()) return () => {}
  const onOnline = () => void flushAnalytics()
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void flushAnalytics()
  }
  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onHidden)
  void flushAnalytics()
  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onHidden)
  }
}
